/**
 * Client-side checks for the creatives operators upload from this screen: the Home modal
 * (exact 900 × 1600) and the banner carousel (landscape, close to 16:9). The backend
 * (kredit-api-MT) enforces the same rules and is the one that counts; these only give
 * feedback before the upload leaves the browser, so they must stay in sync with it.
 */

/** Value for the file input `accept` attribute. */
export const MARKETING_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';

const FORMAT_LABEL_BY_MIME: Record<string, string> = {
    'image/png': 'PNG',
    'image/jpeg': 'JPG',
    'image/webp': 'WebP',
};

/** File extensions the backend accepts for each format; it rejects a name that disagrees with the bytes. */
const EXTENSIONS_BY_MIME: Record<string, string[]> = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/webp': ['.webp'],
};

/** Formats we can recognize but never accept, named for the error message. */
const REJECTED_FORMAT_LABEL: Record<string, string> = {
    'image/gif': 'un GIF',
    'image/bmp': 'un BMP',
    'image/tiff': 'un TIFF',
    'application/pdf': 'un PDF',
};

export interface MarketingImageRules {
    maxBytes: number;
    /** Exact size, for artwork that fills a fixed frame (the modal). */
    exact?: { width: number; height: number };
    /** Floor on the width, for artwork the app scales into a slot (the banners). */
    minWidth?: number;
    /** Expected aspect ratio with a tolerance band; the app crops whatever exceeds it. */
    ratio?: { value: number; tolerance: number; label: string };
    /** Below this width the file passes with a warning. */
    recommendedWidth?: number;
    /** Above this weight the file passes with a warning. */
    recommendedMaxBytes?: number;
}

export interface MarketingImageInfo {
    name: string;
    sizeKb: number;
    /** Human label of the format (PNG, JPG, WebP), or the raw mime type when it is not allowed. */
    format: string;
    /** Natural size in px; null when the browser could not decode the file. */
    width: number | null;
    height: number | null;
}

export interface MarketingImageCheck {
    info: MarketingImageInfo;
    /** Empty when the file can be published. Spanish, ready to show inline. */
    error: string;
    /** Non-blocking remarks, Spanish, ready to show. */
    warnings: string[];
}

/**
 * Loads the image behind an object URL to read its natural size.
 * Rejects when the browser cannot decode the file.
 */
export function readImageDimensions(objectUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
        image.onerror = () => reject(new Error('Image could not be decoded'));
        image.src = objectUrl;
    });
}

/**
 * Format told by the first bytes of the file (its magic number), independent of its name.
 * The browser derives `file.type` from the extension, so a JPEG saved as `.png` reports
 * `image/png`; the backend sniffs the bytes and would reject it after the upload.
 * Returns null when the bytes match no format we know.
 */
export async function sniffImageMime(file: File): Promise<string | null> {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const at = (index: number): number => head[index] ?? -1;
    const startsWith = (...bytes: number[]): boolean => bytes.every((byte, index) => at(index) === byte);

    if (startsWith(0x89, 0x50, 0x4e, 0x47)) return 'image/png';
    if (startsWith(0xff, 0xd8, 0xff)) return 'image/jpeg';
    if (startsWith(0x52, 0x49, 0x46, 0x46) && at(8) === 0x57 && at(9) === 0x45 && at(10) === 0x42 && at(11) === 0x50) {
        return 'image/webp';
    }
    if (startsWith(0x47, 0x49, 0x46, 0x38)) return 'image/gif';
    if (startsWith(0x42, 0x4d)) return 'image/bmp';
    if (startsWith(0x49, 0x49, 0x2a, 0x00) || startsWith(0x4d, 0x4d, 0x00, 0x2a)) return 'image/tiff';
    if (startsWith(0x25, 0x50, 0x44, 0x46)) return 'application/pdf';
    return null;
}

function fileExtension(name: string): string {
    const dot = name.lastIndexOf('.');
    return dot === -1 ? '' : name.slice(dot).toLowerCase();
}

function formatMegabytes(bytes: number): string {
    return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',');
}

function formatPercent(fraction: number): string {
    return (fraction * 100).toFixed(1).replace('.', ',');
}

/**
 * Validates format (by content), extension, weight and dimensions, in that order; the first
 * failure wins. `objectUrl` must have been created from `file` by the caller, who also owns
 * revoking it.
 */
export async function checkMarketingImage(
    file: File,
    objectUrl: string,
    rules: MarketingImageRules
): Promise<MarketingImageCheck> {
    const sniffed = await sniffImageMime(file);
    const mime = sniffed ?? file.type;
    const formatLabel = FORMAT_LABEL_BY_MIME[mime];
    const info: MarketingImageInfo = {
        name: file.name,
        sizeKb: Math.round(file.size / 1024),
        format: formatLabel || mime || 'desconocido',
        width: null,
        height: null,
    };
    const warnings: string[] = [];

    if (!formatLabel) {
        const known = REJECTED_FORMAT_LABEL[mime];
        return {
            info,
            error: known ? `El archivo es ${known}: usa PNG, JPG o WebP` : 'Formato no permitido: usa PNG, JPG o WebP',
            warnings,
        };
    }

    // The backend compares the name with the bytes: say it here, before the upload leaves the browser.
    const extension = fileExtension(file.name);
    const allowed = EXTENSIONS_BY_MIME[mime] ?? [];
    if (!allowed.includes(extension)) {
        const named = extension ? `se llama ${extension}` : 'no tiene extensión';
        return {
            info,
            error: `El archivo es ${formatLabel} pero ${named}: renómbralo a ${allowed[0]} antes de subirlo`,
            warnings,
        };
    }

    if (file.size > rules.maxBytes) {
        const maxMb = rules.maxBytes / (1024 * 1024);
        return { info, error: `El archivo pesa ${formatMegabytes(file.size)} MB y el máximo son ${maxMb} MB`, warnings };
    }

    let width: number;
    let height: number;
    try {
        ({ width, height } = await readImageDimensions(objectUrl));
    } catch {
        return { info, error: 'No se pudo leer la imagen: verifica que el archivo no esté dañado', warnings };
    }
    info.width = width;
    info.height = height;
    const size = `esta mide ${width} × ${height}`;

    if (rules.exact && (width !== rules.exact.width || height !== rules.exact.height)) {
        return {
            info,
            error: `La imagen debe medir exactamente ${rules.exact.width} × ${rules.exact.height} px (${size})`,
            warnings,
        };
    }

    if (rules.minWidth !== undefined && width < rules.minWidth) {
        return { info, error: `La imagen debe tener al menos ${rules.minWidth} px de ancho (${size} px)`, warnings };
    }

    if (rules.ratio) {
        const ratio = width / height;
        const lowest = rules.ratio.value * (1 - rules.ratio.tolerance);
        const highest = rules.ratio.value * (1 + rules.ratio.tolerance);
        if (ratio < lowest || ratio > highest) {
            return {
                info,
                error: `La imagen debe ser apaisada, cercana a ${rules.ratio.label} (${size} px, proporción ${ratio.toFixed(2).replace('.', ',')}:1)`,
                warnings,
            };
        }
        // Centered crop: what exceeds the slot on the longer side is what the customer never sees.
        const crop = ratio > rules.ratio.value ? 1 - rules.ratio.value / ratio : 1 - ratio / rules.ratio.value;
        if (crop > 0.01) {
            warnings.push(
                `No es ${rules.ratio.label} exacto: la app recorta un ${formatPercent(crop)} % ${ratio > rules.ratio.value ? 'del ancho' : 'del alto'}`
            );
        }
    }

    if (rules.recommendedWidth !== undefined && width < rules.recommendedWidth) {
        warnings.push(
            `Ancho de ${width} px: por debajo de los ${rules.recommendedWidth} px recomendados, en pantallas grandes se verá algo suave`
        );
    }

    if (rules.recommendedMaxBytes !== undefined && file.size > rules.recommendedMaxBytes) {
        warnings.push(`Pesa ${info.sizeKb} KB: por encima de los ${Math.round(rules.recommendedMaxBytes / 1024)} KB recomendados`);
    }

    return { info, error: '', warnings };
}
